import Keycloak from 'keycloak-js'

import { isMetrikaEmbedding } from '@/helpers/isMetrikaEmbedding'
import { $themeConfig } from '@themeConfig'

import { setAuthState } from './authState'
import { clearDataAndRedirect, logout } from './authUtils'
import keycloakConfig from './keycloak/keycloakConfig'
import { getUserData } from './utils'

class AuthService {
    constructor() {
        this.keycloak = null
        this.isInitialized = false
        this.axiosInstance = null
        this.tokenRefreshPromise = null
    }

    syncAuthState() {
        const userData = getUserData()
        setAuthState({
            isAuthenticated: this.isAuthenticated(),
            user: userData ? userData.user : null,
            permissions: userData ? userData.permissions : [],
        })
    }

    async init(axiosInstance) {
        if (this.isInitialized) {
            return this.isAuthenticated()
        }

        this.axiosInstance = axiosInstance

        try {
            console.log('🔑 Initializing Keycloak...')
            this.keycloak = new Keycloak({
                url: keycloakConfig.url,
                realm: keycloakConfig.realm,
                clientId: keycloakConfig.clientId,
            })

            const inMetrika = isMetrikaEmbedding()
            const isLocalhost = ['localhost', '127.0.0.1', '::1'].includes(
                window.location.hostname
            )

            const storedToken = localStorage.getItem('accessToken') || undefined
            const storedRefresh =
                localStorage.getItem('refreshToken') || undefined

            const baseOpts = {
                pkceMethod: 'S256',
                silentCheckSso: true,
                silentCheckSsoRedirectUri: `${window.location.origin}/silent-check-sso.html`,
                checkLoginIframe: !isLocalhost,
                token: storedToken,
                refreshToken: storedRefresh,
            }

            const localOpts = {
                ...baseOpts,
                onLoad: 'login-required',
                silentCheckSso: false,
                checkLoginIframe: false,
            }

            const metrikaOpts = {
                ...baseOpts,
                onLoad: 'check-sso',
                silentCheckSso: true,
                checkLoginIframe: true,
            }

            const normalOpts = {
                ...baseOpts,
                onLoad: 'login-required',
                silentCheckSso: true,
                checkLoginIframe: true,
            }

            const opts = isLocalhost
                ? localOpts
                : inMetrika
                ? metrikaOpts
                : normalOpts

            const authenticated = await this.keycloak.init(opts)

            console.log(
                '🔑 Keycloak initialized, authenticated:',
                authenticated
            )

            if (authenticated) {
                await this.handleSuccessfulAuth()
                this.isInitialized = true
                return true
            }

            this.syncAuthState()

            if (inMetrika) {
                window.__READ_ONLY__ = true // флаг для роутов/UI
                this.isInitialized = true
                return false
            }

            clearDataAndRedirect(this)

            return false
        } catch (error) {
            console.error('Keycloak init error:', error)
            if (!isMetrikaEmbedding()) {
                clearDataAndRedirect(this)
            } else {
                this.isInitialized = true
            }
            throw error
        }
    }

    async handleSuccessfulAuth() {
        try {
            this.saveTokens()
            await this.fetchUserProfile()
            this.syncAuthState()
        } catch (error) {
            console.error('Error handling successful auth:', error)
            clearDataAndRedirect(this)
        }
    }

    saveTokens() {
        if (this.keycloak?.token) {
            localStorage.setItem('accessToken', this.keycloak.token)
        }
        if (this.keycloak?.refreshToken) {
            localStorage.setItem('refreshToken', this.keycloak.refreshToken)
        }
    }

    async fetchUserProfile() {
        try {
            const response = await this.axiosInstance.get(
                `${$themeConfig.app.backUrlAuth}/user-profile`
            )

            const userData = response.data
            console.log('👤 User profile received:', userData)

            localStorage.setItem(
                'userData',
                JSON.stringify({
                    ...userData,
                    ability: [{ action: 'read', subject: 'Auth' }],
                })
            )

            window.dispatchEvent(new CustomEvent('user-data-updated'))
        } catch (error) {
            console.error('❌ Error fetching user profile:', error)
            throw error
        }
    }

    async updateToken(minValidity = 30, force = false) {
        if (!this.keycloak) {
            throw new Error('Keycloak instance not available for token update.')
        }
        if (this.tokenRefreshPromise) {
            const refreshed = await this.tokenRefreshPromise
            if (!force || refreshed) return refreshed
        }
        if (!this.tokenRefreshPromise) {
            this.tokenRefreshPromise = this.keycloak
                .updateToken(force ? -1 : minValidity)
                .then(refreshed => {
                    if (refreshed) {
                        console.log(
                            '✅ Token refreshed successfully by interceptor.'
                        )
                        this.saveTokens()
                    }
                    return refreshed
                })
                .catch(async error => {
                    console.error(
                        '❌ Failed to refresh token. Logging out.',
                        error
                    )
                    await this.logout()
                    throw error
                })
                .finally(() => {
                    this.tokenRefreshPromise = null
                })
        }
        return this.tokenRefreshPromise
    }

    getToken() {
        return localStorage.getItem('accessToken')
    }

    isAuthenticated() {
        return !!this.keycloak?.token
    }

    async login(options = {}) {
        if (!this.keycloak) {
            this.keycloak = new Keycloak({
                url: keycloakConfig.url,
                realm: keycloakConfig.realm,
                clientId: keycloakConfig.clientId,
            })
        }
        const currentTheme =
            localStorage.getItem('vuexy-skin') || $themeConfig.layout.skin

        const currentUrl = new URL(window.location.href)
        currentUrl.searchParams.set('theme', currentTheme)

        return this.keycloak.login({
            ...options,
            redirectUri: currentUrl.toString(),
        })
    }

    async logout() {
        await logout(this.keycloak)
        this.syncAuthState()
    }
}

export default new AuthService()
